import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ASQPowerBIReport from '../../../../common/components/molecules/ASQPowerBIReport';
import {
  expirationTokenInterval,
  useDepartureDissatisfiedPassengerReportsQuery,
} from '../../../../common/hooks/backend';
import { useAsqAccess } from '../../../../common/utils/accessHelper';
import { getReportChangeListenerForFiltersAndSlicers } from '../../../../common/utils/power-bi-filters';
import { Module } from '../../../../core/enums/module.enum';
import { withFeatureFlag } from '../../../../core/featureFlags/FeatureFlag';
import { FeatureFlagKey } from '../../../../core/featureFlags/featureFlagKeys';
import { useSetCurrentPowerBiFilters } from '../../../../core/powerBi/hooks';
import DashboardHeader from '../../dashboards/components/molecules/DashboardHeader';
import {
  getDepartureDissatisfiedPassengerInfoMessageTranslateKey,
  getDepartureDissatisfiedPassengerTitleTranslateKey,
  useDepartureDissatisfiedPassengerDashboard,
} from '../dashboards/dissatisfiedPassenger';

function DashboardsDissatisfiedPassenger(): ReactElement {
  const { access } = useAsqAccess();
  const { t } = useTranslation('Departure');

  const { dashboard, pageName, onPageChange, onSlicerChange, synchronizeAirportCodeAndSubscriptionAndPeriod } =
    useDepartureDissatisfiedPassengerDashboard(Module.Departure);

  const { data: embedRequest, isLoading } = useDepartureDissatisfiedPassengerReportsQuery(undefined, {
    pollingInterval: expirationTokenInterval,
  });

  const infoMessageTranslateKey = getDepartureDissatisfiedPassengerInfoMessageTranslateKey(dashboard);

  const [, setCurrentPowerBiFilters] = useSetCurrentPowerBiFilters();
  const onRendered = getReportChangeListenerForFiltersAndSlicers(setCurrentPowerBiFilters);

  if (!access?.additionalAccess?.dissatisfiedPassenger?.departures?.length) {
    return <></>;
  }

  return (
    <>
      {dashboard !== null && (
        <DashboardHeader
          title={t(getDepartureDissatisfiedPassengerTitleTranslateKey(dashboard))}
          infoMessage={infoMessageTranslateKey !== null ? t(infoMessageTranslateKey) : undefined}
        />
      )}
      <ASQPowerBIReport
        embedRequest={embedRequest}
        isLoading={isLoading}
        pageName={pageName ?? undefined}
        onPageChange={onPageChange}
        onLoaded={synchronizeAirportCodeAndSubscriptionAndPeriod}
        onSlicerChange={onSlicerChange}
        onRendered={onRendered}
        showFilters={false}
      />
    </>
  );
}

const DashboardsDissatisfiedPassengerFeature = withFeatureFlag(
  DashboardsDissatisfiedPassenger,
  FeatureFlagKey.DepartureReports,
);

export default DashboardsDissatisfiedPassengerFeature;
